import { QUEUE_TYPE, THandleSetFilter, TQueueFilter } from "@elixir/types";
import { getQueueKeyFromQueueType } from "@elixir/utils";
import { Dropdown } from "antd";
import React, { PropsWithChildren, useMemo } from "react";
import { FilterMenuComponentUi, useGetFilterOptions } from "ui";
import useAllQueuesQuery from "../../apis/tryst/get-all-queues";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import { track } from "../../utils/tracking";

const Filter = ({
  children,
  queueType,
  queueFilter,
  setQueueFilter,
}: PropsWithChildren<{
  queueType: QUEUE_TYPE | string;
  queueFilter: TQueueFilter;
  setQueueFilter: (filter: TQueueFilter) => void;
}>) => {
  const { data: queueConfigData } = useGetQueueConfig();
  const { data: queueData } = useAllQueuesQuery({ queueType: "all" });

  const handleSetFilter: THandleSetFilter = React.useCallback(
    ({ type, value }) => {
      const queueToModify = queueFilter[type];
      const indexOfFilter = queueToModify.findIndex(
        (filterValue) => filterValue === value
      );

      if (indexOfFilter > -1) {
        setQueueFilter({
          ...queueFilter,
          [type]: [
            ...queueToModify.slice(0, indexOfFilter),
            ...queueToModify.slice(indexOfFilter + 1),
          ],
        });
        return;
      }
      setQueueFilter({
        ...queueFilter,
        [type]: [...queueToModify, value],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queueFilter]
  );

  const handleChipPress = (id: keyof TQueueFilter, key: string | number) => {
    handleSetFilter({ type: id, value: key });
  };

  const filterOptions = useGetFilterOptions({
    queueData:
      queueData?.[
        [QUEUE_TYPE.BOOKED, QUEUE_TYPE.FOLLOWUP, QUEUE_TYPE.OTHERS].includes(
          queueType as QUEUE_TYPE
        )
          ? getQueueKeyFromQueueType(queueType)
          : queueType
      ],
    tagRecords: queueConfigData?.data?.tags || [],
    queueType,
  });

  const [visible, setVisible] = React.useState(false);

  const handleVisibleChange = (flag: boolean) => {
    track("filter", {});
    setVisible(flag);
  };

  const handleClose = () => {
    handleVisibleChange(false);
  };

  const menu = useMemo(
    () => (
      <FilterMenuComponentUi
        handleClose={handleClose}
        filterOptions={filterOptions}
        handleChipPress={handleChipPress}
        queueFilter={queueFilter}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterOptions, queueFilter]
  );
  return (
    <Dropdown
      destroyPopupOnHide
      onOpenChange={handleVisibleChange}
      trigger={["click"]}
      overlay={menu}
      open={visible}
      overlayClassName="dr-w-366 dr-max-h-448 dr-overflow-y-scroll dr-rounded-8 dr-shadow-elevation-900"
      className=""
    >
      {children}
    </Dropdown>
  );
};

export default Filter;
